import React from 'react';
import { Grid } from '@mui/material';

import { useUpdateEffect, useFormState } from 'shared/hooks';
import useConsultData from 'features/consults/hooks/useConsultData';
import useConsultInitialFormState from 'features/consults/hooks/useConsultInitialFormState';
import { CONSULT_TABS } from 'features/consults/utils/constants';
import { getInitialValueByFieldType } from 'features/consults/utils/formFields';

import GridWrapper from 'shared/components/layout/GridWrapper/GridWrapper';
import QuestionsPanel from 'features/consults/components/tabFields/QuestionsPanel';
import MRSPanel from 'features/consults/shared/tabs/Plan/MRSPanel';

import formFields, {
  thrombolyticValues,
  doseValues,
} from 'features/consults/shared/tabs/Plan/formFields';

import schema from './validationSchema';
import {
  thrombolyticElement,
  thrombolyticElements,
  extendedWindowExclusionElements,
  extendedWindowExclusionThrombolyticValues,
  intraarterialExclusionElements,
  intraarterialExclusionThrombolyticValues,
  getVisiblePlanFields,
  relativeContraindicationsElements,
  candidateElements,
  rightColumnElements,
} from './data';

const IVIA = () => {
  const {
    control,
    register,
    getFieldError,
    getValues,
    setFormValues,
    watch,
    setValue,
  } = useFormState(schema, { stateSlice: 'consults', showAsSnackbar: true });

  const {
    consult,
    consultFacility,
    consultTabData,
    isEditable,
    isMacro,
    handleFieldChange,
    saveConsultChange,
  } = useConsultData(CONSULT_TABS.PLAN, getValues);

  // The new 2026 AIS sections persist on their own ivIa data slice.
  const { consultTabData: iviaTabData, handleFieldChange: handleIviaFieldChange } =
    useConsultData(CONSULT_TABS.IVIA, getValues);

  // "Possible Thrombolytic Candidate" was moved here from HPI but keeps its
  // HPI data slice, so it saves through the HPI-bound change handler.
  const { consultTabData: hpiTabData, handleFieldChange: handleHpiFieldChange } =
    useConsultData(CONSULT_TABS.HPI, getValues);

  const watchedValues = watch();
  const currentThrombolyticValue =
    watchedValues[formFields.thrombolyticInterventionPredefined.name];

  const thrombolyticSection = thrombolyticElements[currentThrombolyticValue];

  const showExtendedWindowExclusion =
    extendedWindowExclusionThrombolyticValues.includes(currentThrombolyticValue);

  const showIntraarterialExclusion =
    intraarterialExclusionThrombolyticValues.includes(currentThrombolyticValue);

  const legacyExclusionFields = [
    formFields.thrombolyticExclusionThreeToFourPointFiveHourWindowPredefined
      .name,
    formFields.thrombolyticExclusionPredefined.name,
    formFields.thrombolyticExclusionFreeText.name,
  ];
  const hasLegacyExclusionData = legacyExclusionFields.some((name) => {
    const value = consultTabData[name];
    return Array.isArray(value) ? value.length > 0 : value != null && value !== '';
  });

  // Retired sections: hidden on new consults, kept for historical data.
  const visibleThrombolyticSection =
    hasLegacyExclusionData || !thrombolyticSection
      ? thrombolyticSection
      : thrombolyticSection.filter(
          ({ name }) => !legacyExclusionFields.includes(name),
        );

  useConsultInitialFormState(
    consult.id,
    { ...consultTabData, ...iviaTabData, ...hpiTabData },
    setFormValues,
  );

  const defaultDose = React.useMemo(
    () => doseValues[consultFacility?.thrombolytic],
    [consultFacility?.thrombolytic],
  );

  const updateDosing = React.useCallback(() => {
    const values = getValues();

    if (
      !defaultDose ||
      values.dose !== null ||
      ![
        thrombolyticValues.IVThrombolytic,
        thrombolyticValues.IVThrombolyticExtendedWindow,
        thrombolyticValues.IVAndIAIntervention,
        thrombolyticValues.IVThrombolyticPossibleIACandidate,
      ].includes(currentThrombolyticValue)
    )
      return;

    setValue(formFields.dose.name, defaultDose);
    handleFieldChange(formFields.dose.name);
  }, [
    currentThrombolyticValue,
    defaultDose,
    getValues,
    setValue,
    handleFieldChange,
  ]);

  useUpdateEffect(() => {
    updateDosing();
  }, [updateDosing]);

  const handleThrombolyticChange = React.useCallback(
    (fieldName) => {
      const newThrombolyticValue = getValues(fieldName);

      if (!newThrombolyticValue) {
        saveConsultChange({
          [fieldName]: newThrombolyticValue,
        });
        return;
      }

      const newFields = getVisiblePlanFields(newThrombolyticValue).map(
        ({ name }) => name,
      );

      const resetValues = getVisiblePlanFields(currentThrombolyticValue).reduce(
        (res, { name, type }) =>
          newFields.includes(name)
            ? res
            : { ...res, [name]: getInitialValueByFieldType(type) },
        {},
      );

      const resetValuesFixed = Object.keys(resetValues).includes(
        formFields.dose.name,
      )
        ? resetValues
        : {
            ...resetValues,
            [formFields.dose.name]: getValues(formFields.dose.name),
          };

      setFormValues({
        ...watchedValues,
        ...resetValuesFixed,
        [fieldName]: newThrombolyticValue,
      });

      saveConsultChange({
        ...resetValuesFixed,
        [fieldName]: newThrombolyticValue,
      });
    },
    [
      currentThrombolyticValue,
      getValues,
      saveConsultChange,
      setFormValues,
      watchedValues,
    ],
  );

  return (
    <GridWrapper>
      <Grid item sm={12} lg={6}>
        <QuestionsPanel
          control={control}
          register={register}
          onChange={handleThrombolyticChange}
          getFieldError={getFieldError}
          disabled={!isEditable}
          elements={thrombolyticElement}
        />
        <QuestionsPanel
          control={control}
          register={register}
          onChange={handleFieldChange}
          getFieldError={getFieldError}
          disabled={!isEditable}
          elements={visibleThrombolyticSection}
          disableElements={
            isMacro
              ? [
                  formFields.timeIAInterventionRecommended.name,
                  formFields.timeIVThrombolyticRecommendedMountainTime.name,
                ]
              : []
          }
        />
        {showExtendedWindowExclusion && (
          <QuestionsPanel
            control={control}
            register={register}
            onChange={handleIviaFieldChange}
            getFieldError={getFieldError}
            disabled={!isEditable}
            elements={extendedWindowExclusionElements}
          />
        )}
        {showIntraarterialExclusion && (
          <QuestionsPanel
            control={control}
            register={register}
            onChange={handleFieldChange}
            getFieldError={getFieldError}
            disabled={!isEditable}
            elements={intraarterialExclusionElements}
          />
        )}
        {currentThrombolyticValue && (
          <QuestionsPanel
            control={control}
            register={register}
            onChange={handleIviaFieldChange}
            getFieldError={getFieldError}
            disabled={!isEditable}
            elements={relativeContraindicationsElements}
          />
        )}
      </Grid>
      <Grid item sm={12} lg={6}>
        <QuestionsPanel
          control={control}
          register={register}
          onChange={handleHpiFieldChange}
          getFieldError={getFieldError}
          disabled={!isEditable}
          elements={candidateElements}
        />
        <QuestionsPanel
          control={control}
          register={register}
          onChange={handleIviaFieldChange}
          getFieldError={getFieldError}
          disabled={!isEditable}
          elements={rightColumnElements}
        />
        <MRSPanel
          form={{
            control,
            register,
            setValue,
            getValues,
            getFieldError,
          }}
          saveConsultChange={saveConsultChange}
          disabled={!isEditable}
        />
      </Grid>
    </GridWrapper>
  );
};

export default IVIA;
